import { Select } from '../../../design-system';
import { TASK_STATUSES, PRIORITIES, dueAging } from '../../../lib/taskStatus';
import { clientOf, typeOf, assigneeOf } from '../../../lib/taskViews';

// ⚠️ THE REFERENCE NUMBER LIVES IN THIS VIEW AND ONLY THIS ONE. The old table
// had an ID column and people quote "TASK-0042" to each other, so it could not
// simply vanish with the redesign — but a list and a board are for scanning,
// and a monospace code on every card would cost more attention than it repays.
// The dense view is where a reference belongs.
const taskRef = (n) => (n != null ? `TASK-${String(n).padStart(4, '0')}` : '—');

// The dense table, with the fields people actually change editable in place.
//
// ⚠️ THE EDITABLE SET IS DELIBERATELY SHORT: status, priority, assignee, due
// date. Those are the four a manager changes while reading the list, and each
// is a single scalar the server already accepts on `PATCH /api/tasks/:id`.
// Client and category are NOT editable here — moving a task to another client
// re-points its billing, its compliance instance and its access rules, and a
// dropdown in a 28px row is the wrong place to ask for that.
//
// ⚠️ THE ROW IS NOT A FORM AND THERE IS NO SAVE BUTTON. Each control patches on
// change and the page rolls the row back if the server refuses. A table of
// dropdowns with a save button somewhere else is how people lose edits.

// A <input type="date"> wants YYYY-MM-DD, in LOCAL time. `toISOString()` is UTC
// and shifts the date back a day for anybody east of Greenwich — which is every
// user of this app.
const dateInputValue = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

function TaskTableView({ tasks, selectedIds, onToggle, onOpen, onPatch, onDelete, staff, canEdit }) {
  return (
    <div className="tv-table-wrap">
      <table className="tv-table">
        <thead>
          <tr>
            <th className="tv-th-check" aria-label="Select" />
            <th className="tv-th-ref">ID</th>
            <th>Task</th>
            <th>Client</th>
            <th>Category</th>
            <th>Assignee</th>
            <th>Due</th>
            <th>Status</th>
            <th>Priority</th>
            <th className="tv-th-open" aria-label="Open" />
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const age = dueAging(t);
            const who = assigneeOf(t);
            const type = typeOf(t);
            return (
              <tr key={t._id} className={`tv-tr tv-edge-${age.tone}`}>
                <td className="tv-td-check">
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(t._id)}
                    onChange={() => onToggle(t._id)}
                    aria-label={`Select ${t.title}`}
                  />
                </td>

                <td className="tv-td-ref">{taskRef(t.taskNumber)}</td>

                <td className="tv-td-title">
                  <button type="button" className="tv-cell-link" onClick={() => onOpen(t)}>
                    {t.title}
                  </button>
                </td>

                <td className="tv-td-muted">{clientOf(t)?.displayName || 'Internal'}</td>
                <td className="tv-td-muted">{type?.name || 'Others'}</td>

                <td>
                  {canEdit ? (
                    <Select
                      className="tv-cell-select"
                      value={who?._id || ''}
                      aria-label={`Assignee of ${t.title}`}
                      onChange={(e) => onPatch(t, { preparerId: e.target.value || null })}
                    >
                      <option value="">Unassigned</option>
                      {/* ⚠️ The current assignee is carried as an option even if
                          they are not in `staff` — somebody who has since been
                          deactivated still holds the task, and a <select> whose
                          value matches no option renders BLANK, which reads as
                          unassigned. */}
                      {who && !staff.some((s) => s._id === who._id) && (
                        <option value={who._id}>{who.name} (inactive)</option>
                      )}
                      {staff.map((s) => (
                        <option key={s._id} value={s._id}>{s.name}</option>
                      ))}
                    </Select>
                  ) : (who?.name || 'Unassigned')}
                </td>

                <td>
                  {canEdit ? (
                    <input
                      type="date"
                      className="ds-input tv-cell-date"
                      value={dateInputValue(t.dueDate)}
                      aria-label={`Due date of ${t.title}`}
                      onChange={(e) => onPatch(t, { dueDate: e.target.value || null })}
                    />
                  ) : (
                    <span className={`tv-tone-${age.tone}`}>{dateInputValue(t.dueDate) || '—'}</span>
                  )}
                  <span className={`tv-due-age tv-tone-${age.tone}`}>{age.label}</span>
                </td>

                <td>
                  {canEdit ? (
                    <Select
                      className="tv-cell-select"
                      value={t.status}
                      aria-label={`Status of ${t.title}`}
                      onChange={(e) => onPatch(t, { status: e.target.value })}
                    >
                      {TASK_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </Select>
                  ) : t.status}
                </td>

                <td>
                  {canEdit ? (
                    <Select
                      className="tv-cell-select"
                      value={t.priority || 'medium'}
                      aria-label={`Priority of ${t.title}`}
                      onChange={(e) => onPatch(t, { priority: e.target.value })}
                    >
                      {PRIORITIES.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </Select>
                  ) : t.priority}
                </td>

                <td className="tv-td-open">
                  <button
                    type="button"
                    className="tv-open-btn"
                    onClick={() => onOpen(t)}
                    aria-label={`Open ${t.title}`}
                    title="Open task"
                  >
                    ↗
                  </button>
                  {/* ⚠️ KEPT FROM THE TABLE THIS REPLACED. It was the only
                      delete on the page — BulkActionBar has none — so dropping
                      it while swapping the view in would have removed a
                      capability under cover of a redesign. */}
                  {canEdit && (
                    <button
                      type="button"
                      className="tv-open-btn tv-del-btn"
                      onClick={() => onDelete(t._id)}
                      aria-label={`Delete ${t.title}`}
                      title="Delete task"
                    >
                      ✕
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default TaskTableView;
